/**
 * OpenTableVerificationCode - Displays the verification code for OpenTable integration
 * Users copy this code and paste it in the OpenTable Guest Center to complete onboarding
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, Check, RefreshCw, AlertCircle, ExternalLink } from 'lucide-react'
import { Button } from '@/components/base/buttons'
import { AlertDialog } from '@/components/ui/AlertDialog'
import { useOpenTableVerification } from '@/hooks/api/useOpenTableVerification'

interface OpenTableVerificationCodeProps {
  assistantId: number
}

export function OpenTableVerificationCode({ assistantId }: OpenTableVerificationCodeProps) {
  const { t } = useTranslation()
  const { verificationCode, loading, error, rotating, rotateError, fetchVerificationCode, rotatePartnerId } =
    useOpenTableVerification()
  const [copied, setCopied] = useState(false)
  const [showRotateConfirm, setShowRotateConfirm] = useState(false)

  useEffect(() => {
    if (assistantId) {
      fetchVerificationCode(assistantId)
    }
  }, [assistantId, fetchVerificationCode])

  const handleCopy = async () => {
    if (verificationCode?.partner_id) {
      try {
        await navigator.clipboard.writeText(verificationCode.partner_id)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy:', err)
      }
    }
  }

  const handleRefresh = () => {
    if (assistantId) {
      fetchVerificationCode(assistantId)
    }
  }

  const handleRotateConfirm = async () => {
    setShowRotateConfirm(false)
    if (assistantId) {
      await rotatePartnerId(assistantId)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary" />
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {t('resources.assistants.integrations.opentable.loading')}
        </span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-amber-500" />
          <span className="text-sm text-amber-700 dark:text-amber-400">
            {t('resources.assistants.integrations.opentable.error_fetching')}
          </span>
        </div>
        <button
          onClick={handleRefresh}
          className="rounded-lg p-2 text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/40"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
    )
  }

  if (!verificationCode) {
    return null
  }

  return (
    <div className="space-y-4">
      {/* Step 1: Verification Code */}
      <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
        <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.assistants.integrations.opentable.step1_title')}
        </h4>
        <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
          {t('resources.assistants.integrations.opentable.verification_description')}
        </p>

        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
          <code className="flex-1 font-mono text-lg font-semibold text-primary">
            {verificationCode.partner_id}
          </code>

          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
            title={
              copied
                ? t('resources.assistants.integrations.opentable.copied')
                : t('resources.assistants.integrations.opentable.copy_to_clipboard')
            }
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 text-green-500" />
                <span className="text-green-600 dark:text-green-400">
                  {t('resources.assistants.integrations.opentable.copied')}
                </span>
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                <span>{t('resources.assistants.integrations.opentable.copy_to_clipboard')}</span>
              </>
            )}
          </button>

          <button
            onClick={() => setShowRotateConfirm(true)}
            disabled={rotating}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700"
            title={t('resources.assistants.integrations.opentable.refresh')}
          >
            <RefreshCw className={`h-4 w-4 ${rotating ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {rotateError && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">
            {t('resources.assistants.integrations.opentable.rotate_error')}
          </p>
        )}
      </div>

      <AlertDialog
        isOpen={showRotateConfirm}
        onClose={() => setShowRotateConfirm(false)}
        onConfirm={handleRotateConfirm}
        variant="warning"
        title={t('resources.assistants.integrations.opentable.rotate_confirm_title')}
        description={t('resources.assistants.integrations.opentable.rotate_confirm_description')}
        confirmLabel={t('resources.assistants.integrations.opentable.rotate_confirm_label')}
        cancelLabel={t('components.confirm_dialog.cancel')}
      />

      {/* Step 2: Navigate to OpenTable */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
        <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.assistants.integrations.opentable.step2_title')}
        </h4>
        <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
          {t('resources.assistants.integrations.opentable.step2_description')}
        </p>
        <Button
          type="button"
          color="secondary"
          size="sm"
          iconTrailing={ExternalLink}
          onPress={() => window.open('https://guestcenter.opentable.com/', '_blank')}
        >
          {t('resources.assistants.integrations.opentable.open_guest_center')}
        </Button>
      </div>

      {/* Step 3: Enter Code */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
        <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.assistants.integrations.opentable.step3_title')}
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('resources.assistants.integrations.opentable.step3_description')}
        </p>
      </div>

      {/* Step 4: Confirm */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
        <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.assistants.integrations.opentable.step4_title')}
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('resources.assistants.integrations.opentable.step4_description')}
        </p>
      </div>
    </div>
  )
}
